import React, { useState } from 'react';
import { 
  FaShieldAlt, FaLock, FaUserShield, FaEye, FaFileContract, FaEnvelope, FaChevronDown 
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What personal and financial information does Lotoswealth collect?",
      a: "We collect personal details such as your name, contact information, email address, residential address, date of birth, financial and investment preferences, and required documentation (such as PAN, KYC records, tax forms, and bank statements) strictly to deliver our wealth management, investment planning, and financial advisory services."
    },
    {
      q: "How is my personal and financial data protected?",
      a: "We implement robust security measures, including data encryption (SSL), secure server infrastructure, firewalls, and strict internal access controls, to safeguard your sensitive financial information from unauthorized access, alteration, or disclosure."
    },
    {
      q: "Do you share my data with third parties?",
      a: "We do not sell or rent your personal information. Your data is only shared with trusted financial institutions, partner banks, housing finance companies, mutual fund houses, or statutory regulatory bodies strictly when necessary to process your requested services, such as home loans, mutual fund investments, or tax filings."
    },
    {
      q: "How can I update, review, or delete my personal information?",
      a: "You can review, update, or request the deletion of your personal details and account records at any time by contacting our support team directly through our website's Contact Us page."
    }
  ];

  return (
    <main className="privacy-policy-page">
      <Helmet>
        {/* Basic SEO */}
        <title>Privacy Policy | Data Protection & Client Security | Lotoswealth</title>

        <meta
          name="description"
          content="Read the comprehensive Privacy Policy of Lotoswealth. Learn how we collect, use, protect, and secure your personal and financial information while providing expert wealth management and advisory services."
        />

        <meta
          name="keywords"
          content="Privacy Policy, Lotoswealth Privacy, Data Protection, Financial Data Security, Client Confidentiality, Wealth Management Privacy, Lotoswealth"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Lotoswealth" />
        <meta name="language" content="English" />

        <link
          rel="canonical"
          href="https://www.lotoswealth.com/privacy-policy"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lotoswealth" />
        <meta
          property="og:url"
          content="https://www.lotoswealth.com/privacy-policy"
        />
        <meta
          property="og:title"
          content="Privacy Policy | Lotoswealth"
        />
        <meta
          property="og:description"
          content="Learn how Lotoswealth protects your personal and financial information with high standards of security and confidentiality."
        />
        <meta
          property="og:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Privacy Policy | Lotoswealth"
        />
        <meta
          name="twitter:description"
          content="Review Lotoswealth's privacy commitments and data security practices to ensure your financial journey remains confidential and secure."
        />
        <meta
          name="twitter:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />
      </Helmet>

      {/* =========================================
          1. HERO SECTION
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '60vh', minHeight: '450px', overflow: 'hidden', backgroundColor: '#0a192f' }}>
        
        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 opacity-25">
          <img 
            src="/images/lotoswealth-bg.jpg" 
            alt="Privacy Policy Background" 
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
              Privacy Policy
          </h1>
          <p className="hero-description animate-slide-up delay-2 text-white mx-auto" style={{ maxWidth: '800px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            At Lotoswealth, we value your trust and are deeply committed to protecting your personal and financial privacy. Read below to understand how we handle and safeguard your information.
          </p>
        </div>
      </div>

      {/* =========================================
          2. CORE COMMITMENT SECTION
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 animate-slide-up delay-1">
              <h2 className="fw-bold mb-4 text-dark">
                Our Commitment to Your Data Security
              </h2>
              <p className="text-muted line-height-lg">
                When you choose Lotoswealth for wealth management, investment planning, loans, or financial advisory, you share sensitive personal and financial details with us. We treat this information with the highest degree of confidentiality, integrity, and security.
              </p>
              <p className="text-muted line-height-lg">
                This Privacy Policy outlines what information we gather, how we utilize it, the strict safeguards we enforce, and your rights regarding your personal data.
              </p>
            </div>
            <div className="col-lg-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm border-start border-teal border-5">
                <h4 className="fw-bold text-teal mb-3">Key Privacy Principles</h4>
                <ul className="list-unstyled text-muted mb-0">
                  <li className="mb-2"><FaShieldAlt className="text-teal me-2" /> <strong>Strict Confidentiality:</strong> Your financial records remain secure.</li>
                  <li className="mb-2"><FaShieldAlt className="text-teal me-2" /> <strong>No Data Selling:</strong> We never sell your personal data to advertisers.</li>
                  <li className="mb-2"><FaShieldAlt className="text-teal me-2" /> <strong>Transparent Usage:</strong> Data is used solely to deliver optimal financial services.</li>
                  <li className="mb-2"><FaShieldAlt className="text-teal me-2" /> <strong>Secure Infrastructure:</strong> Advanced encryption protocols protect your assets and data.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          3. DETAILED POLICY CLAUSES (EXPANDED)
      ========================================= */}
      <section className="py-5 bg-white">
        <div className="container py-4" style={{ maxWidth: '900px' }}>
          
          <div className="mb-5 animate-slide-up delay-1">
            <h3 className="fw-bold text-dark mb-3">1. Information We Collect</h3>
            <p className="text-muted">
              To provide customized wealth management, portfolio planning, tax advisory, and loan solutions, we collect several categories of information:
            </p>
            <ul className="text-muted">
              <li className="mb-2"><strong>Personal Identifiers:</strong> Full name, email address, phone number, residential/correspondence address, and date of birth.</li>
              <li className="mb-2"><strong>Financial & KYC Information:</strong> PAN card details, income proofs, bank account statements, tax returns, credit reports, and investment history necessary for loan or asset management processing.</li>
              <li className="mb-2"><strong>Technical Data:</strong> IP addresses, browser types, device information, operating system details, and website interaction analytics gathered via cookies to optimize user experience.</li>
              <li className="mb-2"><strong>Communication Records:</strong> Records of correspondence, emails, chat logs, or consultation notes when you reach out to our support or advisory teams.</li>
            </ul>
          </div>

          <div className="mb-5 animate-slide-up delay-2">
            <h3 className="fw-bold text-dark mb-3">2. How We Use Your Information</h3>
            <p className="text-muted">
              The data collected is utilized exclusively for legitimate business, advisory, and service delivery purposes:
            </p>
            <ul className="text-muted">
              <li className="mb-2">Processing your home loan, car loan, or education loan applications through partner financial institutions.</li>
              <li className="mb-2">Designing customized wealth management, retirement, tax planning, and investment strategies tailored to your financial goals.</li>
              <li className="mb-2">Communicating important account notifications, portfolio performance updates, service alerts, and requested financial reports.</li>
              <li className="mb-2">Complying with applicable legal, regulatory, and statutory compliance frameworks mandated by authorities in India.</li>
              <li className="mb-2">Enhancing website performance, personalizing content, and improving our customer service offerings.</li>
            </ul>
          </div>

          <div className="mb-5 animate-slide-up delay-3">
            <h3 className="fw-bold text-dark mb-3">3. Cookies and Tracking Technologies</h3>
            <p className="text-muted">
              Our website uses cookies and similar tracking technologies to analyze site traffic, remember user preferences, and deliver a seamless browsing experience. You can choose to disable cookies through your browser settings, though doing so may limit certain functionalities of our digital platform.
            </p>
          </div>

          <div className="mb-5 animate-slide-up delay-4">
            <h3 className="fw-bold text-dark mb-3">4. Data Sharing and Third-Party Disclosure</h3>
            <p className="text-muted">
              We respect your privacy and restrict sharing your data. We disclose your information only under strict conditions:
            </p>
            <ul className="text-muted">
              <li className="mb-2"><strong>Financial Partners:</strong> Trusted banks, housing finance companies, mutual fund houses, and asset management companies strictly when processing your loan or investment applications.</li>
              <li className="mb-2"><strong>Legal & Regulatory Obligations:</strong> When required by law, court orders, or statutory regulatory authorities in India to prevent fraud, money laundering, or financial crime.</li>
              <li className="mb-2"><strong>Service Providers:</strong> Third-party technology vendors who assist us in operating our website, conducting secure data storage, or managing communications under strict confidentiality agreements.</li>
            </ul>
          </div>

          <div className="mb-5 animate-slide-up delay-5">
            <h3 className="fw-bold text-dark mb-3">5. Data Retention</h3>
            <p className="text-muted">
              We retain your personal and financial information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, comply with legal obligations, resolve disputes, and enforce our agreements. Once data is no longer required, it is securely archived or permanently deleted.
            </p>
          </div>

          <div className="mb-5 animate-slide-up delay-6">
            <h3 className="fw-bold text-dark mb-3">6. Security Measures</h3>
            <p className="text-muted">
              We utilize state-of-the-art security technologies, including secure sockets layer (SSL) data encryption, firewalls, secure server hosting, and restricted employee access policies, to ensure that your personal and financial records remain safe from unauthorized alteration, disclosure, or destruction.
            </p>
          </div>

          <div className="mb-5 animate-slide-up delay-7">
            <h3 className="fw-bold text-dark mb-3">7. Your Privacy Rights</h3>
            <p className="text-muted">
              You have the right to access, review, or correct your personal data held with us. You may also request the updating or deletion of your profile details by contacting our administrative team. Note that certain financial records must be retained pursuant to statutory compliance laws.
            </p>
          </div>

          <div className="mb-5 animate-slide-up delay-8">
            <h3 className="fw-bold text-dark mb-3">8. Policy Updates</h3>
            <p className="text-muted">
              Lotoswealth reserves the right to update or modify this Privacy Policy periodically to reflect technological advancements, service enhancements, or regulatory adjustments. Any revisions will be posted on this page with an updated revision date.
            </p>
          </div>

        </div>
      </section>

      {/* =========================================
          4. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Common queries regarding our privacy practices</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-white rounded-3 shadow-sm border overflow-hidden"
              >
                <button 
                  className="w-100 text-start p-3 border-0 bg-white d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown 
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`} 
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-light lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          5. FINAL CTA SECTION
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Have Questions About Our Privacy Policy?</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
            If you have any concerns or require further clarification regarding data handling at Lotoswealth, feel free to reach out to our team.
          </p>
          <a href="/contact-us" className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3 text-decoration-none d-inline-block">
            Contact Us Today
          </a>
        </div>
      </section>

    </main>
  );
};

export default PrivacyPolicy;